import { projectMarketPoolResolutionAt } from '@morfi/contract-sdk';
import {
  type MarketPoolQuoteSnapshot,
  type MarketPoolQuoteSnapshotConfig,
  type MarketPoolQuoteSnapshotRow,
  marketPoolQuoteSnapshotFromRow,
} from '@morfi/market-math';
import type { MarketConfig } from '../core/internal/market-config-catalog.js';

export type OnchainTradeData = MarketPoolQuoteSnapshot;
export type OnchainTradeDataByMarket = Readonly<Record<string, OnchainTradeData>>;
export type MarketPoolRow = MarketPoolQuoteSnapshotRow & {
  readonly marketConfig: string;
  readonly resolutionMode: 'conviction' | 'timed';
  readonly tradingStartedAt: string | null;
  readonly tradingEndAt: string | null;
};
export type MarketPlatformConfig = MarketPoolQuoteSnapshotConfig &
  Pick<
    MarketConfig,
    | 'resolutionThresholdFloorMarginPpb'
    | 'resolutionThresholdDecayDelaySecs'
    | 'resolutionThresholdDropIntervalSecs'
    | 'resolutionThresholdDropPpb'
  >;

export interface PoolSnapshotQueryState {
  readonly isError: boolean;
  readonly isFetching: boolean;
  readonly isStale: boolean;
}

export function onchainTradeDataFromMarketRows(
  rows: readonly MarketPoolRow[],
  configs: Readonly<Record<string, MarketPlatformConfig>>,
): OnchainTradeDataByMarket {
  const snapshots: Record<string, OnchainTradeData> = {};
  for (const row of rows) {
    try {
      snapshots[row.pubkey] = marketPoolSnapshotFromMarketRow(row, configs[row.marketConfig]);
    } catch {
      // The batch result reports this row as unavailable while retaining valid siblings.
    }
  }
  return snapshots;
}

export function onchainTradeDataFromMarketRow(
  row: MarketPoolRow,
  config: MarketPlatformConfig | undefined,
): OnchainTradeData {
  return marketPoolSnapshotFromMarketRow(row, config);
}

export function marketPoolSnapshotFromMarketRow(
  row: MarketPoolRow,
  config: MarketPlatformConfig | undefined,
): MarketPoolQuoteSnapshot {
  if (row.status === 'trading' && config === undefined)
    throw new Error('market configuration unavailable');
  if (row.status !== 'trading') return marketPoolQuoteSnapshotFromRow(row, config ?? {});
  const tradingStart = unixSeconds(row.tradingStartedAt, 'trading start');
  const snapshot = marketPoolQuoteSnapshotFromRow(row, {
    ...config,
    resolutionMode: row.resolutionMode,
    tradingStartedUnixSeconds: tradingStart,
    ...(row.resolutionMode === 'timed'
      ? { tradingEndUnixSeconds: unixSeconds(row.tradingEndAt, 'trading end') }
      : {}),
  });
  const gate = snapshot.bondingCurveTradeGate;
  if (gate === null) throw new Error('trading market has no conviction gate');
  if (row.resolutionMode === 'conviction') {
    Object.assign(gate, {
      tradingStartedUnixSeconds: tradingStart,
      resolutionThresholdFloorMarginPpb: requiredPolicy(config?.resolutionThresholdFloorMarginPpb),
      resolutionThresholdDecayDelaySecs: requiredPolicy(config?.resolutionThresholdDecayDelaySecs),
      resolutionThresholdDropIntervalSecs: requiredPolicy(
        config?.resolutionThresholdDropIntervalSecs,
      ),
      resolutionThresholdDropPpb: requiredPolicy(config?.resolutionThresholdDropPpb),
    });
    gate.resolutionAtUnixSeconds = projectMarketPoolResolutionAt(snapshot);
  }
  return snapshot;
}

function unixSeconds(value: string | null, name: string): string {
  const timestamp = value === null ? Number.NaN : Date.parse(value);
  if (!Number.isFinite(timestamp) || timestamp < 0)
    throw new Error(`market is missing valid ${name}`);
  return String(Math.floor(timestamp / 1000));
}

function requiredPolicy(value: string | undefined): string {
  if (value === undefined) throw new Error('market configuration is missing resolution policy');
  return value;
}

export function isPoolSnapshotUnavailable(state: PoolSnapshotQueryState): boolean {
  // TanStack staleness schedules reconciliation; it does not invalidate an
  // already projected, identity-checked pool snapshot.
  return state.isError;
}
